import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "../redux/reducers/Models";
import { debtsPulling, requestDebts, setUser } from "../redux/ducks/user";
import { setCustomer } from "../redux/ducks/customer";
import { Debts, getFormattedDebts, FormattedDebts } from "@/utils/transform";
import { getIsValidString } from "@/utils/functions";
import { PropsView } from "../pages/home/Models";

export const useDebts = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(true);
  let TIMEOUT = 2000;

  const user = useSelector((state: ReducersTypes & {
    userReducer: ReducersTypes['userReducer'] & {
      cart: PropsView['selectables'];
      debts: FormattedDebts[];
    }
  }) => state.userReducer);

  const customer = useSelector((state: ReducersTypes & {
    customerReducer: {
      client?: { id: string };
      vehicles?: { plate: string }[];
      debts?: FormattedDebts[];
      cart?: PropsView['selectables'];
      requestId?: string;
    }
  }) => state.customerReducer);

  // Get the appropriate plate based on authentication state
  const currentPlate = customer.selectedVehicles ? customer.selectedVehicles?.toString() : customer?.vehicles?.[0]?.plate;
  const currentDebts = customer?.debts || user?.debts;
  const currentCart = customer?.cart || user?.cart;
  const currentRequestId = customer?.requestId || user?.requestId;

  const handleDebtsPulling = (success: boolean, code?: string | Debts[]) => {
    if (!success) {
      if (code === "again" && shouldPoll) {
        setTimeout(() => {
          TIMEOUT = TIMEOUT * 2;
          startPulling();
        }, TIMEOUT);
        return;
      }
      setIsLoading(false);
      setShouldPoll(false);
      dispatch(
        setUser({
          debts: [],
        })
      );
      return;
    }

    const formattedDebts = getFormattedDebts(code as Debts[]);
    
    // Update the appropriate state based on authentication
    if (customer?.client?.id) {
      dispatch(
        setCustomer({
          debts: formattedDebts,
          cart: formattedDebts.map(item => ({ name: item.id, checked: true }))
        })
      );
    } else {
      dispatch(
        setUser({
          debts: formattedDebts,
          cart: formattedDebts.map(item => ({ name: item.id, checked: true }))
        })
      );
    }

    setIsLoading(false);
    setShouldPoll(false);
  };

  const startPulling = (requestId?: string) => {
    if (!isLoading) setIsLoading(true);

    dispatch(
      debtsPulling({
        requestId: getIsValidString(requestId as string) ? requestId as string : currentRequestId as string,
        callback: handleDebtsPulling,
      })
    );
  };

  const handleRequestDebts = (success: boolean, code?: string) => {
    if (!success) {
      setIsLoading(false);
      setShouldPoll(false);
      
      if (customer?.client?.id) {
        dispatch(
          setCustomer({
            debts: []
          })
        );
      } else {
        dispatch(
          setUser({
            debts: [],
          })
        );
      }
      return;
    }

    // Update the appropriate state based on authentication
    if (customer?.client?.id) {
      dispatch(
        setCustomer({
          requestId: code
        })
      );
    } else {
      dispatch(
        setUser({
          requestId: code,
        })
      );
    }
    setShouldPoll(true);
  };

  const getDebts = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setShouldPoll(true);

    dispatch(
      requestDebts({
        plate: currentPlate ?? '',
        callback: handleRequestDebts,
      })
    );
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    if (name === "all") {
      const newCart = checked && currentDebts ? currentDebts.map(item => ({ name: item.id, checked: true })) : null;
      
      if (customer?.client?.id) {
        dispatch(
          setCustomer({
            cart: newCart
          })
        );
      } else {
        dispatch(
          setUser({
            cart: newCart,
          })
        );
      }
      return;
    }

    const updatedSelectables = currentCart ? [...currentCart.filter(item => item.name !== name)] : [];

    if (checked) {
      updatedSelectables.push({ name, checked });
    }

    if (customer?.client?.id) {
      dispatch(
        setCustomer({
          cart: updatedSelectables.length ? updatedSelectables : null
        })
      );
    } else {
      dispatch(
        setUser({
          cart: updatedSelectables.length ? updatedSelectables : null
        })
      );
    }
  };

  useEffect(() => {
    if (getIsValidString(currentRequestId as string) && isLoading && shouldPoll) {
      startPulling(currentRequestId as string);
    }
  }, [currentRequestId, shouldPoll]);

  useEffect(() => {
    if (currentPlate || (Array.isArray(customer.vehicles) && customer.vehicles.length === 0)) getDebts();
  }, [currentPlate, customer?.client?.id, customer?.vehicles]);

  return {
    isLoading,
    debts: currentDebts,
    cart: currentCart,
    plate: currentPlate,
    onChange,
    getDebts,
  };
};
